import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useApp } from "../../contexts/AppContext";
import { showAuthAlert } from "../../utils/authAlerts";
import { useNavigate } from 'react-router-dom';
import { 
  getUserMovieInteraction,
  markAsWatched,
  unmarkAsWatched,
  addToWatchlist,
  removeFromWatchlist,
  toggleFavorite
} from "../../services/movieInteractionApi";
import { trackAction } from "../../utils/optimizedTracking";
import { achievementTriggers } from "../../utils/achievementTriggers";
import { 
  IconButton, 
  Tooltip, 
  Box, 
  Chip,
  CircularProgress,
  Fade,
  Zoom,
  Typography
} from '@mui/material';
import {
  BookmarkBorder,
  Bookmark,
  Visibility,
  VisibilityOff,
  Delete,
  Add,
  PlaylistAdd
} from '@mui/icons-material';
import WatchlistSelector from '../Watchlist/WatchlistSelector';

export default function MovieActionButtons({ movie }) {
  const { user, setUser, refreshUser, isRefreshing, theme } = useAuth();
  const { addNotification } = useApp();
  const navigate = useNavigate();
  const [loadingWatchlist, setLoadingWatchlist] = useState(false);
  const [loadingWatched, setLoadingWatched] = useState(false);
  const [hoverWatchlist, setHoverWatchlist] = useState(false);
  const [hoverWatched, setHoverWatched] = useState(false);
  const [error, setError] = useState(null);
  const [watchlistSelectorOpen, setWatchlistSelectorOpen] = useState(false);

  const userId = user?.id || user?._id;
  const isLoggedIn = Boolean(userId);

  const [interaction, setInteraction] = useState(null);

  useEffect(() => {
    if (isLoggedIn && movie?.id) {
      getUserMovieInteraction(userId, movie.id)
        .then(setInteraction)
        .catch(console.error);
    }
  }, [isLoggedIn, userId, movie?.id]);

  const isInWatchlist = interaction?.inWatchlist || false;
  const isWatched = interaction?.watched || false;
  


const toggleWatchlist = useCallback(async (e) => {
  e.stopPropagation();
  setError(null);
  
  if (!isLoggedIn) {
    showAuthAlert(addNotification, navigate, 'WATCHLIST');
    return;
  }
  
  if (!movie?.id || loadingWatchlist) return;

  if (isInWatchlist) {
    setLoadingWatchlist(true);
    try {
      const updatedInteraction = await removeFromWatchlist(userId, movie.id);
      setInteraction(updatedInteraction);
      
      addNotification({
        type: 'success',
        message: `Removed "${movie.name || movie.title}" from watchlist`
      });
      
      await trackAction(movie.id, 'MOVIE', 'remove_from_watchlist', { id: userId }, {
        source: 'action_buttons',
        movieName: movie.name || movie.title
      });
    } catch (err) {
      console.error('Error removing from watchlist:', err);
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingWatchlist(false);
    }
  } else {
    setWatchlistSelectorOpen(true);
  }
}, [isLoggedIn, movie?.id, loadingWatchlist, isInWatchlist, userId, movie?.name, movie?.title, addNotification, navigate]);

const handleAddToWatchlists = useCallback(async (selectedWatchlists, content, contentType) => {
  try {
    // Refresh interaction state after watchlist changes
    const updatedInteraction = await getUserMovieInteraction(userId, movie.id);
    setInteraction(updatedInteraction);

    await trackAction(movie.id, 'MOVIE', 'add_to_watchlist', { id: userId }, {
      source: 'watchlist_selector',
      movieName: movie.name || movie.title,
      watchlistCount: selectedWatchlists.length
    });

    if (userId) {
      await achievementTriggers.addToWatchlist(userId, movie.id);
    }
    
    addNotification({
      type: 'success',
      message: `Updated watchlists for "${movie.name || movie.title}"`
    });
  } catch (err) {
    console.error('Error in handleAddToWatchlists:', err);
  }
}, [userId, movie?.id, movie?.name, movie?.title, addNotification]);

    
const toggleWatched = useCallback(async (e) => {
  e.stopPropagation();
  setError(null);
  
  if (!isLoggedIn) {
    showAuthAlert(addNotification, navigate, 'WATCHLIST');
    return;
  }
  
  if (!movie?.id || loadingWatched) return;

  setLoadingWatched(true);
  const action = isWatched ? 'unmark_watched' : 'mark_watched';
  
  try {
    const updatedInteraction = isWatched 
      ? await unmarkAsWatched(userId, movie.id)
      : await markAsWatched(userId, movie.id);
    
    setInteraction(updatedInteraction);
    
    await trackAction(movie.id, 'MOVIE', action, { id: userId }, {
      source: 'action_buttons',
      movieName: movie.name || movie.title
    });

    if (!isWatched && userId) {
      await achievementTriggers.markWatched(userId, movie.id, movie.name || movie.title);
    }
    
    addNotification({
      type: 'success',
      message: isWatched 
        ? `Unmarked "${movie.name || movie.title}" as watched`
        : `Marked "${movie.name || movie.title}" as watched`
    });
  } catch (err) {
    console.error(`Error in ${action}:`, err);
    setError(err.message);
    setTimeout(() => setError(null), 3000);
  } finally {
    setLoadingWatched(false);
  }
}, [isLoggedIn, movie?.id, loadingWatched, isWatched, userId, movie?.name, movie?.title, addNotification, navigate]);

  if (!movie?.id) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {/* Watchlist Button */}
      <Tooltip 
        title={!isLoggedIn ? "Sign in to add to watchlist" : 
               isInWatchlist ? (hoverWatchlist ? "Remove from watchlist" : "In watchlist") :
               "Add to watchlist"}
        arrow
        TransitionComponent={Zoom}
      >
        <span>
          <IconButton
            onClick={toggleWatchlist}
            onMouseEnter={() => setHoverWatchlist(true)}
            onMouseLeave={() => setHoverWatchlist(false)}
            disabled={!isLoggedIn || loadingWatchlist}
            sx={{
              bgcolor: isInWatchlist ? 'warning.main' : 'transparent',
              color: isInWatchlist ? 'white' : 'text.secondary',
              border: '2px solid',
              borderColor: isInWatchlist ? 'warning.main' : 'divider',
              '&:hover': {
                bgcolor: isInWatchlist 
                  ? (hoverWatchlist ? 'error.main' : 'warning.dark')
                  : 'action.hover',
                borderColor: isInWatchlist 
                  ? (hoverWatchlist ? 'error.main' : 'warning.dark')
                  : 'primary.main',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease-in-out',
              width: 44,
              height: 44,
            }}
          >
            {loadingWatchlist ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Fade in={true} timeout={300}>
                {isInWatchlist ? (
                  hoverWatchlist ? <Delete /> : <Bookmark />
                ) : (
                  <PlaylistAdd />
                )}
              </Fade>
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Watched Button */}
      <Tooltip 
        title={!isLoggedIn ? "Sign in to mark as watched" : 
               isWatched ? (hoverWatched ? "Mark as unwatched" : "Watched") : 
               "Mark as watched"}
        arrow
        TransitionComponent={Zoom}
      >
        <span>
          <IconButton
            onClick={toggleWatched}
            onMouseEnter={() => setHoverWatched(true)}
            onMouseLeave={() => setHoverWatched(false)}
            disabled={!isLoggedIn || loadingWatched}
            sx={{
              bgcolor: isWatched ? 'success.main' : 'transparent',
              color: isWatched ? 'white' : 'text.secondary',
              border: '2px solid',
              borderColor: isWatched ? 'success.main' : 'divider',
              '&:hover': {
                bgcolor: isWatched 
                  ? (hoverWatched ? 'warning.main' : 'success.dark')
                  : 'action.hover',
                borderColor: isWatched 
                  ? (hoverWatched ? 'warning.main' : 'success.dark')
                  : 'primary.main',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease-in-out',
              width: 44,
              height: 44,
            }}
      >
            {loadingWatched ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Fade in={true} timeout={300}>
                {isWatched ? (
                  hoverWatched ? <VisibilityOff /> : <Visibility />
                ) : (
                  <Visibility sx={{ opacity: 0.6 }} />
                )}
              </Fade>
            )}
          </IconButton>
        </span>
      </Tooltip>
      
      {/* Status Chips */}
      {(isInWatchlist || isWatched) && (
        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
          {isInWatchlist && (
            <Chip
              label="Watchlist"
              size="small"
              color="warning"
        variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
          {isWatched && (
            <Chip
              label="Watched"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Chip
          label={error}
          size="small"
          color="error"
          variant="filled"
          sx={{ fontSize: '0.7rem', ml: 1 }}
        />
      )}
      
      {/* Watchlist Selector Dialog */}
      <WatchlistSelector
        open={watchlistSelectorOpen}
        onClose={() => setWatchlistSelectorOpen(false)}
        content={movie}
        contentType="MOVIE"
        onAddToWatchlist={handleAddToWatchlists}
      />
    </Box>
  );
}